import React, { Suspense } from "react"
import { Routes, Route } from "react-router-dom"
import { ProtectedRoute } from "../components/protectedRoutes"
import { LoadingSpinner } from "../components/ui/LoadingSpinner"
import { AdminLayout, ComptableLayout, CashierLayout } from "../components/layout/LayoutComponents"
import { RoleBasedRoute, LoginRedirect, CashierRouteGuard } from "./route-utils"

// Import all lazy-loaded components
import {
    ProductsPage,
    PosPage,
    BrandsPage,
    ShippingPage,
    OrdersPage,
    StockPage,
    UsersPage,
    SalesPage,
    ClientsPage,
    NotFoundPage,
    SubCategoryPage,
    SupplierPage,
    InventoryPage,
    StatisticsPage,
    AccountingPage,
    SettingsPage,
    RefundsPage,
    ProfilePage,
    RestrictedAccess,
    CashierPage,
    DashboardPage,
    ContactPage,
    CategoryPage,
    AuthContainer,
    ReservationPage, // Use this consistent name from lazy-imports
    OrderOnlinePage // Add OrderOnlinePage import
} from "./lazy-imports"
import { ProblemsPage } from "../pages/ProblemsPage"

export const AppRoutes = () => {
    return (
        <Routes>
            {/* Public route for authentication - Using AuthContainer instead of AuthPage */}
            <Route
                path="/"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <AuthContainer />
                    </Suspense>
                }
            />

            {/* Role-based redirect after login */}
            <Route
                path="/login-redirect"
                element={
                    <ProtectedRoute>
                        <LoginRedirect />
                    </ProtectedRoute>
                }
            />

            {/* Restricted access route */}
            <Route
                path="/restricted-access"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <RestrictedAccess />
                    </Suspense>
                }
            />

            {/* Cashier Dashboard - main landing page */}
            <Route
                path="/cashier"
                element={
                    <ProtectedRoute>
                        <CashierRouteGuard>
                            <Suspense fallback={<LoadingSpinner />}>
                                <CashierPage />
                            </Suspense>
                        </CashierRouteGuard>
                    </ProtectedRoute>
                }
            />

            {/* Cashier Module Routes */}
            <Route
                element={
                    <ProtectedRoute>
                        <CashierRouteGuard>
                            <CashierLayout />
                        </CashierRouteGuard>
                    </ProtectedRoute>
                }
            >
                <Route path="/cashier/pos" element={<PosPage />} />
                <Route path="/cashier/refunds" element={<RefundsPage />} />
                <Route path="/cashier/sales" element={<SalesPage />} />
                <Route path="/cashier/clients" element={<ClientsPage />} />
                <Route path="/cashier/profile" element={<ProfilePage />} />
            </Route>

            {/* Comptable routes */}
            <Route
                element={
                    <ProtectedRoute>
                        <RoleBasedRoute element={<ComptableLayout />} allowedRoles={["comptable"]} />
                    </ProtectedRoute>
                }
            >
                {/* Dashboard route */}
                <Route path="/comptable" element={<StatisticsPage />} />
                <Route path="/comptable/accounting" element={<AccountingPage />} />
                <Route path="/comptable/profile" element={<ProfilePage />} />

                {/* Stock Routes */}
                <Route path="/comptable/stock">
                    <Route path="management" element={<StockPage />} />
                    <Route path="inventory" element={<InventoryPage />} />
                    <Route path="suppliers" element={<SupplierPage />} />
                </Route>

                {/* Comptability Routes */}
                <Route path="/comptable/comptability">
                    <Route path="statistics" element={<StatisticsPage />} />
                    <Route path="accounting" element={<AccountingPage />} />
                </Route>
            </Route>

            {/* Admin routes - protected from caissier role */}
            <Route
                element={
                    <ProtectedRoute>
                        <AdminLayout />
                    </ProtectedRoute>
                }
            >
                {/* Dashboard route */}
                <Route
                    path="/admin"
                    element={<RoleBasedRoute element={<DashboardPage />} allowedRoles={["admin", "manager"]} />}
                />

                <Route
                    path="/admin/profile"
                    element={<RoleBasedRoute element={<ProfilePage />} allowedRoles={["admin", "manager", "cashier", "comptable"]} />}
                />

                {/* Entities Routes */}
                <Route path="/admin/entities">
                    <Route
                        path="products"
                        element={<RoleBasedRoute element={<ProductsPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="category"
                        element={<RoleBasedRoute element={<CategoryPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="subcategory"
                        element={<RoleBasedRoute element={<SubCategoryPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="brand"
                        element={<RoleBasedRoute element={<BrandsPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="shipping"
                        element={<RoleBasedRoute element={<ShippingPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="clients"
                        element={<RoleBasedRoute element={<ClientsPage />} allowedRoles={["admin", "manager"]} />}
                    />
                </Route>

                {/* 
                <Route
                    path="/admin/reservations"
                    element={<RoleBasedRoute element={<ReservationPage />} allowedRoles={["admin", "manager"]} />}
                /> */}

                {/* Cash Register Routes */}
                <Route path="/admin/cash-register">
                    <Route
                        path="pos"
                        element={<RoleBasedRoute element={<PosPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="refunds"
                        element={<RoleBasedRoute element={<RefundsPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="sales"
                        element={<RoleBasedRoute element={<SalesPage />} allowedRoles={["admin", "manager"]} />}
                    />
                </Route>

                {/* Store Routes
                <Route path="/admin/store">
                    <Route
                        path="orders"
                        element={<RoleBasedRoute element={<OrderOnlinePage />} allowedRoles={["admin", "manager"]} />}
                    />
                </Route> */}

                {/* Stock Routes */}
                <Route path="/admin/stock">
                    <Route
                        path="management"
                        element={<RoleBasedRoute element={<StockPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="inventory"
                        element={<RoleBasedRoute element={<InventoryPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="suppliers"
                        element={<RoleBasedRoute element={<SupplierPage />} allowedRoles={["admin", "manager"]} />}
                    />
                </Route>

                {/* Comptability Routes */}
                <Route path="/admin/comptability">
                    <Route
                        path="statistics"
                        element={<RoleBasedRoute element={<StatisticsPage />} allowedRoles={["admin", "manager"]} />}
                    />

                    <Route
                        path="accounting"
                        element={<RoleBasedRoute element={<AccountingPage />} allowedRoles={["admin", "manager"]} />}
                    />
                </Route>

                {/* Issues & Support */}
                <Route path="/admin/issues">
                    <Route
                        index
                        element={<RoleBasedRoute element={<ContactPage />} allowedRoles={["admin", "manager"]} />}
                    />
                    <Route
                        path="list"
                        element={<RoleBasedRoute element={<ProblemsPage />} allowedRoles={["admin"]} />}
                    />
                </Route>

                {/* Users Routes */}
                <Route
                    path="/admin/users"
                    element={<RoleBasedRoute element={<UsersPage />} allowedRoles={["admin"]} />}
                />

                {/* Settings Routes */}
                <Route
                    path="/admin/settings"
                    element={<RoleBasedRoute element={<SettingsPage />} allowedRoles={["admin"]} />}
                />
            </Route>

            {/* Catch-all route for 404 */}
            <Route
                path="*"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <NotFoundPage />
                    </Suspense>
                }
            />
        </Routes>
    )
}